/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { POTENCIA_MINIMA_VERTICAL, PROPOSTA_OPTIONS } from "src/components/projetos/constants";
import { ModalSimulacaoFinanciamento } from "src/components/projetos/containers/ModalSimulacaoFinanciamento";
import { OpcaoServicoSuspensa } from "src/components/projetos/containers/OpcaoServicoSuspensa";
import { ResumoProjeto, ResumoProjetoRow } from "src/components/projetos/containers/ResumoProjeto";
import { TermosProjeto } from "src/components/projetos/containers/TermosProjeto";
import { ComponenteCalcular, FATURAMENTO_TIPOS, FaturamentoType, GeradorItem, IFaturamentoTipos, Projeto, ProjetoCalcular, ResultadoCalculos } from "src/components/projetos/types";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { IDataResponse } from "src/framework/providers/api";
import { ProjetosProvider } from "src/providers/ProjetosProvider";
import theme from "src/theme/light";
import { InputsSection } from "src/components/projetos/containers/InputsSection";
import { LayoutProjetoKit } from "../../_layouts";
import { formatarComponentesCalcular } from "src/components/projetos/utils/formatarComponentesCalcular";
import { Info } from "@mui/icons-material";
import { setServicoInformationModalStatus } from "src/store/vendas/projetos/actions";
import { validacaoValorServicoMinimo } from "src/components/projetos/utils/validarValorServicoMinimo";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { alterandoServico$, calculoProjetoRealizado$, exibeJustificativaValorServico$ } from "src/store/vendas/projetos/store";
import { mostrarAvisoServicoRemovido } from "src/components/projetos/utils/mostrarAvisoServicoRemovido";
import { CardsList } from "src/components/projetos/personalizados/containers/Finalizacao/containers/Card";
import { CalcularType } from "src/components/projetos/personalizados";
import { ModalFaturamento } from "src/components/projetos/personalizados/containers/Finalizacao/containers/ModalFaturamento";
import { checarParametroRegiaoPotencia } from "src/components/projetos/utils/checarParametroRegiaoPotencia";
import { notifierStore } from "src/framework/store/NotifierStore";
import { useObservable } from "src/framework/store/storeUtil";

interface ValorTotalProps {
  formState: Projeto;
  onChangeFormState: Dispatch<SetStateAction<Projeto>>
  onClickNext: () => void;
  onClickBack: () => void;
}

const projetosProvider = new ProjetosProvider()

let timerCalcular: NodeJS.Timeout

function ValorTotal({ formState, onChangeFormState, onClickBack, onClickNext }: ValorTotalProps) {
  const permissoes = PermissionUtil.getPermissions();
  const [simulacaoModalIsOpened, setSimulacaoModalIsOpened] = useState(false)
  const [textoTooltipDisabled, setTextoTooltipDisabled] = useState<string>("Para salvar o projeto é necessário aceitar os termos do projeto.");
  const [valorMinimoServico, setValorMinimoServico] = useState(0)
  const [modalFaturamento, setModalFaturamento] = useState({ status: false, imgUrl: '', title: '' })
  const [calculando, setCalculando] = useState(false)

  const calculoProjetoRealizado = useObservable(calculoProjetoRealizado$);
  const alterandoServico = useObservable(alterandoServico$);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const setFormState = useCallback((newState: Partial<Projeto>) => {
    onChangeFormState(old => ({...old, ...newState}))
  },[onChangeFormState])

  const potenciaTotal = useMemo(()=>{
    return Number(formState?.resultado_calculos?.componentes?.potencia_gerador || 0)
  },[formState?.resultado_calculos])

  useEffect(()=>{
    if (formState.isEdit && Number(formState.valor_servico || 0) > 0 && !formState?.revenda?.segmentos_servico?.includes(formState?.segmento?.includes("on") ? "on" : "off")) {
      setFormState({
        valor_servico: 0,
        valor_servico_pis_cofins: 0,
        servico: null,
        codigo_servico: "",
        justificativa_valor_servico: null,
        tipo_proposta: PROPOSTA_OPTIONS.find(item => item.value === 'gerador-sem-instalacao')
      })
      
      mostrarAvisoServicoRemovido()
    }
  },[])

  useEffect(()=>{
    if (potenciaTotal > 0) {
      projetosProvider.calcularValorMinimoServico(potenciaTotal || 0).then((response) => {
        setValorMinimoServico(response.data?.valor_minimo_servico || 0)
      })
    }
  },[potenciaTotal])

  useEffect(()=>{
    if ((formState?.valor_servico || 0) > 0 && valorMinimoServico > 0 && !validacaoValorServicoMinimo(formState?.valor_servico || 0, valorMinimoServico, permissoes)) {
      notifierStore.setNotifier({
        title: 'Atenção!',
        content: [
          'O valor do serviço não atende às exigências vigentes.',
          'Por favor, atualize conforme as instruções fornecidas abaixo do campo "Valor do Serviço Líquido”.'
        ],
        timeOut: 4000,
        onClose: () => notifierStore.setOpen(false),
      });
      notifierStore.setOpen(true);
    }
  },[valorMinimoServico, potenciaTotal])

  const potenciaVertical = useMemo<boolean>(() => {
    return potenciaTotal > POTENCIA_MINIMA_VERTICAL
  },[potenciaTotal]);

  const servicoObrigatorio = useMemo(()=>{
    if (potenciaVertical) return false;
    
    const moduloSelecionado = formState?.modulosList?.find(item => item.selecionado);
    if (!checarParametroRegiaoPotencia(moduloSelecionado)?.status) return false;

    return formState.tipo_faturamento === 'cliente' && formState.tipo_proposta?.value === 'gerador-com-instalacao'
  },[potenciaVertical, formState.tipo_proposta?.value])

  const mostrarCampoValorServico = useMemo(()=>{
    return !!(servicoObrigatorio && formState.recebe_servico) 
  },[servicoObrigatorio, formState.recebe_servico])

  const exibirJustificativaValorServico = useMemo(() => {
    if (!PermissionUtil.can(PermissionUtil.PERMISSION_PROJETOS_EDITAR_ESPECIAL_SERVICO, permissoes)) return false;
    if (!mostrarCampoValorServico) return false;

    const valorServicoProjeto = Number(formState?.valor_servico || 0)

    if (valorServicoProjeto === 0) return false;
    if (Number(valorMinimoServico || 0) === 0) return false;

    return valorServicoProjeto < valorMinimoServico;
  },[formState?.valor_servico, mostrarCampoValorServico, permissoes, valorMinimoServico])

  useEffect(() => {
    exibeJustificativaValorServico$.next(exibirJustificativaValorServico)
  },[exibirJustificativaValorServico])

  const nextButtonIsDisabled = useMemo(() => {
    if (!formState.tipo_proposta?.value) {
      setTextoTooltipDisabled("Para salvar o projeto é necessário preencher o tipo de proposta.");
      return true;
    }
    if (servicoObrigatorio && !formState.valor_servico) {
      setTextoTooltipDisabled("Para salvar o projeto é necessário preencher o valor do serviço.");
      return true;
    }
    if (servicoObrigatorio && formState.valor_servico && Number(formState.valor_servico) === 0) {
      setTextoTooltipDisabled("Para salvar o projeto é necessário preencher o valor do serviço.");
      return true;
    }
    if (!formState.termo_aceito) {
      setTextoTooltipDisabled("Para salvar o projeto é necessário aceitar os termos do projeto.");
      return true;
    }
    if ((formState?.valor_servico || 0) > 0 && !validacaoValorServicoMinimo(formState?.valor_servico || 0, valorMinimoServico, permissoes)) {
      setTextoTooltipDisabled(`Para finalizar o projeto é necessário inserir o valor mínimo do serviço`);
      return true;
    }
    if (exibirJustificativaValorServico && !(formState?.justificativa_valor_servico || "")) {
      setTextoTooltipDisabled(`Para finalizar o projeto é necessário inserir a justificativa do valor de serviço`);
      return true;
    }
    if (!calculoProjetoRealizado) {
      setTextoTooltipDisabled(`Para finalizar o projeto é necessário realizar o cálculo dos valores do projeto.`);
      return true;
    }
    if (alterandoServico || calculando) {
      setTextoTooltipDisabled(`Aguarde o calculo finalizar para prosseguir.`);
      return true;
    }
    return false;
  },[exibirJustificativaValorServico, formState?.justificativa_valor_servico, formState.termo_aceito, valorMinimoServico, formState.tipo_proposta?.value, formState.valor_servico, servicoObrigatorio, calculoProjetoRealizado, alterandoServico, calculando])

  const resumoProjeto = useMemo((): ResumoProjetoRow[] =>{
    return [
      { label: 'Título', value: formState.titulo || '' },
      { label: 'Peso do gerador (Kg)', value: `${(formState.resultado_calculos?.componentes?.peso_gerador || 0).toLocaleString()} (Kg)` },
      { label: 'Peso do gerador no telhado (Kg)', value: `${(formState.resultado_calculos?.componentes?.peso_telhado || 0).toLocaleString()} (Kg)` }
    ]
  }, [formState.resultado_calculos?.componentes?.peso_gerador, formState.resultado_calculos?.componentes?.peso_telhado, formState.titulo])

  const valoresCalculos = useMemo((): { total: number; totalComponentes: number; totalServico: number; } =>{
    const totalComponentes = Number(formState.resultado_calculos?.componentes?.valor || 0)
    const totalServico = Number(formState.resultado_calculos?.servicos?.valor_com_pis_cofins || 0)
    return {total: totalServico + totalComponentes, totalComponentes, totalServico }
  },[formState.resultado_calculos?.componentes?.valor, formState.resultado_calculos?.servicos?.valor_com_pis_cofins])

  const secaoValores = useMemo((): ResumoProjetoRow[] =>{
    return [
      { label: 'Valor dos produtos', value: Formater.toString(valoresCalculos.totalComponentes, TypeRecords.CURRENCY) },
      { label: (
        <Box display="flex" alignItems={isMobile ? "end" : "center"}>
          <Typography style={{ display: 'block', textAlign: 'start' }} variant='subtitle1'>Valor do serviço da revenda com PIS/COFINS</Typography>
          <Box onClick={() => setServicoInformationModalStatus(true)} component={'button'} style={{ background: 'none', cursor: 'pointer' }} border={"none"}>
            <Info color="primary" fontSize={"small"} />
          </Box> 
        </Box>
      ),
        value: Formater.toString(valoresCalculos.totalServico || 0, TypeRecords.CURRENCY),
        hide: Number(valoresCalculos.totalServico || 0) === 0,
      },
      { label: 'Valor total', value: <strong style={{ color: theme.palette.primary.main }}>{Formater.toString(valoresCalculos.total, TypeRecords.CURRENCY)}</strong> }
    ]
  },[valoresCalculos])

  const calcular = useCallback(async({ valorServico }: CalcularType = {})=>{
    const componentes: ComponenteCalcular[] = []

    const comps: GeradorItem[] = []
    formState.geradores_kits.forEach(gerador => {
      comps.push(...gerador.items)
    })

    componentes.push(...formatarComponentesCalcular(comps))

    const regioesIds = formState.regioes.map(item => item.id)

    const getValorServico = () => {
      if (!formState.recebe_servico) return 0;
      if (formState.tipo_proposta?.value === 'gerador-sem-instalacao') return 0;
      return Number(valorServico ?? formState.valor_servico ?? 0)
    }

    const recalcular = projetosProvider.confereRecalcular(componentes, formState);

    const payload: ProjetoCalcular = {
      carrinho_id: formState.carrinho_id,
      componentes,
      estado_id: formState.estado?.id || '',
      servicos: {
        tipo: getValorServico() ? 'revenda' : null,
        valor: getValorServico(),
      },
      regioes: regioesIds,
      segmento: formState.segmento,
      projeto_id: formState.id || undefined,
      recalcular: recalcular,
    }

    const responseCalcular: IDataResponse<ResultadoCalculos> = await projetosProvider.calcular(payload)

    setFormState({ valor_servico_pis_cofins: responseCalcular.data?.servicos?.valor_com_pis_cofins || 0, resultado_calculos: responseCalcular.data })
  },[formState.geradores_kits, formState.regioes, formState.carrinho_id, formState.estado?.id, formState.segmento, formState.id, formState.recebe_servico, formState.tipo_proposta?.value, formState.valor_servico, setFormState])

  useEffect(()=>{
    clearTimeout(timerCalcular)
    setCalculando(true)
    timerCalcular = setTimeout(()=>{
      calcular().finally(() => setCalculando(false))
    }, 300)
  }, [calcular])

  useEffect(()=>{
    if (formState.recebe_servico === false || !formState.revenda) {
      const opt = PROPOSTA_OPTIONS.find(item => item.value === 'gerador-sem-instalacao') || null
      setFormState({ ...formState, tipo_proposta: opt })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[formState.recebe_servico, setFormState, formState.revenda])
  
  return (
    <>
      <LayoutProjetoKit
        formState={formState}
        nextButtonLabel='Finalizar projeto'
        mostrarPremissas={false}
        onClickBack={onClickBack}
        nextButtonIsDisabled={nextButtonIsDisabled}
        onClickNext={onClickNext}
        title="Faturamento"
        subtitle="Escolha o tipo de faturamento!"
        nextButtonDisabledTooltip={textoTooltipDisabled}
      >
        <Box p={2}>
          <CardsList
            selected={formState.tipo_faturamento}
            onSelect={(tipo) => {
              const state = {...formState}
              const valor = tipo as FaturamentoType
              state.tipo_faturamento = valor

              const _faturamentoTipo = FATURAMENTO_TIPOS.find((f: IFaturamentoTipos) => f.id === valor);

              if(!_faturamentoTipo?.disable){
                if (valor !== 'cliente') {
                  const tipoProposta = PROPOSTA_OPTIONS.find(item => item.value === 'gerador-sem-instalacao')
                  state.tipo_proposta = tipoProposta || null
                }

                if (Number(state.valor_servico || 0) !== 0) {
                  state.valor_servico = 0;
                  state.tempo_garantia_servico = 0
                  calcular({ valorServico: 0 });
                }
                
                onChangeFormState(state)
              }
            }}
            handleClickOnMoreInformations={id => setModalFaturamento({status: true, imgUrl: FATURAMENTO_TIPOS.find(item => item.id === id)?.modal.imgUrl || '', title: FATURAMENTO_TIPOS.find(item => item.id === id)?.modal.title || '' })}
            items={FATURAMENTO_TIPOS.map(({ id, title, subtitle, disable }) => ({ id, title, subtitle, disable }))}
          />
          <Box marginBottom={2}>
            <InputsSection
              formState={formState}
              setFormState={setFormState}
              mostrarCampoValorServico={mostrarCampoValorServico}
              servicoObrigatorio={servicoObrigatorio}
              valorMinimoServico={(formState.valor_servico || 0) > 0 ? valorMinimoServico > 0 && validacaoValorServicoMinimo((formState.valor_servico || 0), valorMinimoServico, permissoes) ? 0 : valorMinimoServico : 0}
              exibirJustificativaValorServico={exibirJustificativaValorServico}
            />
          </Box>
            
            {!formState.recebe_servico && (
              <Box marginBottom={2}>
                <OpcaoServicoSuspensa />
              </Box>
            )}

          <Box marginBottom={2}>
            <TermosProjeto
              checked={formState.termo_aceito}
              setChecked={(selecionado, dados_termo) => setFormState({ termo_aceito: selecionado, dados_termo })}
            />
          </Box>

          <Box marginBottom={2}>
            <ResumoProjeto
              formState={formState}
              simularFuncionamentoClick={() => setSimulacaoModalIsOpened(true)}
              valores={secaoValores}
              resumoProjeto={resumoProjeto}
            />
          </Box>
        </Box>
      </LayoutProjetoKit>
      {simulacaoModalIsOpened && (
        <ModalSimulacaoFinanciamento
          onChangeFormState={(newState: Partial<Projeto>) => {
            setFormState(newState);
          } }
          formState={formState}
          open={simulacaoModalIsOpened}
          onClose={() => setSimulacaoModalIsOpened(false)}
          valorTotal={valoresCalculos.total}
        />
      )}

      {modalFaturamento.status && <ModalFaturamento
        title={modalFaturamento.title}
        imgUrl={modalFaturamento.imgUrl}
        open={modalFaturamento.status}
        onClose={()=> setModalFaturamento({ status: false, imgUrl: '', title: '' })}
      />}
    </>
  )
}
export { ValorTotal }